/**
 * VerdictDashboardPage - Chat-like research interface with persistent sessions
 *
 * Progressive UI States:
 * - State 0 (Idle): Input bar + suggestion chips only
 * - State 1 (Researching): Logic Tree grows (intelligent loading screen)
 * - State 2 (Conversation): Logic Tree stays visible (collapsed), FollowUpChat slides up
 *
 * The Logic Tree IS the loading screen AND remains visible after research completes.
 * This is the key differentiator - continuous context visualization.
 *
 * Free User Access:
 * - Unauthenticated users can access the dashboard via "Try it now"
 * - History sidebar is replaced with a CTA to sign up
 * - Daily newsletter widget is hidden for free users
 * - Follow-up research is disabled for free users
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Loader2, UserPlus, Lock, Menu, Crown, X, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/Tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { LogicTree, FollowUpChat, GeneralResultCard } from '@/components/verdict';
import { ProcessBar } from '@/components/verdict/ProcessBar';
import { GeneralDecisionSheet } from '@/components/verdict/GeneralDecisionSheet';
import { FinancialDecisionSheet } from '@/components/verdict/FinancialDecisionSheet';
import { StrategicDecisionSheet } from '@/components/verdict/StrategicDecisionSheet';
import { getGeneralProcessMetadata, getFinancialProcessMetadata, getStrategicProcessMetadata } from '@/utils/processBarMetadata';
import { restoreFinancialState, restoreStrategicState, hasFullFinancialState } from '@/utils/restoreState';
import { UserMenu } from '@/components/layout/UserMenu';
import { Alert } from '@/components/ui/Alert';
import { Sidebar } from '@/components/layout/Sidebar';
import { ResearchSidebarContent } from '@/components/verdict/ResearchSidebarContent';
import { FreeUserSidebarContent } from '@/components/verdict/FreeUserSidebarContent';
import { ResearchHeroInput, ResearchHeroInputHandle } from '@/components/verdict/ResearchHeroInput';
import { FeatureShowcase, type FeatureDemo } from '@/components/verdict/FeatureShowcase';
import { DebateDemo } from '@/components/verdict/DebateDemo';
import { FactCheckDemo } from '@/components/verdict/FactCheckDemo';
import { FinancialLogicTree } from '@/components/verdict/FinancialLogicTree';
import { StrategicLogicTree } from '@/components/verdict/StrategicLogicTree';
import { FinancialResultCard } from '@/components/verdict/FinancialResultCard';
import { StrategicResultCard } from '@/components/verdict/StrategicResultCard';
import { ComparisonResultCard } from '@/components/verdict/ComparisonResultCard';
import { QuotePopover } from '@/components/verdict/QuotePopover';
import { useFinancialStream } from '@/hooks/useFinancialStream';
import { useStrategicStream, type StrategicStreamingState, type StrategicIntensity, type StrategicDeliverable, type StressTestResult, type StrategicWorkstream, type StrategicConflict, type DebateArgument as StrategicDebateArgument, type MediatorConsensus, type GhostDeckSlide, type CompetitorData } from '@/hooks/useStrategicStream';
import type { FinancialAgentProgress, MediatorCompilation, FinancialStreamingState } from '@/components/verdict/FinancialLogicTree';
import { useResearchSessions } from '@/hooks/useResearchSessions';
import { useResearchSession, type ResearchSession } from '@/hooks/useResearchSession';
import { api, API_BASE_URL } from '@/lib/api';
import { useFingerprint } from '@/hooks/useFingerprint';
import { useCredits } from '@/hooks/useCredits';
import { useIsMobile, useIsTablet } from '@/hooks/useMediaQuery';
import { useProductTour } from '@/hooks/useProductTour';
import { InsufficientCreditsDialog, CreditPurchaseModal, CreditConsumedToast } from '@/components/credits';
import {
  DebateDrawer,
  DebateMinimizedPill,
  DebateTriggerFAB,
  type DebateDrawerMode,
} from '@/components/debate/DebateDrawer';
import { useDebateStream } from '@/hooks/useDebateStream';
import type { ResearchIntensity, ResearchMode } from '@/types';
import { INTENSITY_CREDIT_THRESHOLDS } from '@/types';

// UI State machine - simplified to 3 states
type UIState = 'idle' | 'researching' | 'conversation';

// Helper to parse verdict message content (may be JSON with blueprint or plain text)
interface ParsedVerdict {
  verdict: string;
  total_credits_used?: number;
  // Financial-specific fields (saved by backend)
  kill_sheet?: Record<string, unknown>;
  anchored_snapshot?: Record<string, unknown>;
  blueprint?: {
    topic: string;
    mode?: string;  // 'financial' | 'strategic' | undefined (general)
    // General mode fields
    tasks: Array<{
      name: string;
      description: string;
      execution_mode?: 'parallel' | 'sequential';
      aggregated_output: string;
      confidence_score?: number;
      debate_occurred?: boolean;
      web_sources?: Array<{ url: string; title: string }>;
      agents?: Array<{
        name: string;
        model: string;
        provider: string;
        success: boolean;
        output_preview?: string;
        full_output?: string;
        latency_seconds?: number;
        web_sources_used?: number;
      }>;
      fact_check?: {
        is_validated: boolean;
        key_findings: string[];
        discrepancies: string[];
        model?: string;
        provider?: string;
      } | null;
      debate?: {
        rounds_completed: number;
        agreement_score: number;
        final_consensus?: string;
        mediator_model?: string;
        resolved_discrepancies: string[];
        challenges_tested?: string[];
        challenges_held?: string[];
        watch_items?: string[];
      } | null;
    }>;
    models_used?: string[];
    total_latency_seconds?: number;
    total_web_sources?: number;
    task_count?: number;
    // Financial mode fields (accumulated by backend)
    ticker?: string;
    depth?: string;
    winning_side?: string;
    confidence?: number;
    bull_agents?: unknown[];
    bear_agents?: unknown[];
    debate_messages?: unknown[];
    mediator_compilation?: Record<string, unknown>;
    convergence_trajectory?: number[];
    initial_divergence?: number;
    echo_chamber_detected?: boolean;
    // Strategic mode fields
    strategic_result_id?: number;
  };
}

function parseVerdictContent(content: string): ParsedVerdict {
  try {
    const parsed = JSON.parse(content);
    if (parsed && typeof parsed.verdict === 'string') {
      return parsed as ParsedVerdict;
    }
  } catch {
    // Not JSON, treat as plain text verdict (legacy format)
  }
  return { verdict: content };
}

// Unified type for completed research results of any mode
type HistoricalResult =
  | { type: 'general'; session: ResearchSession }
  | { type: 'financial'; killSheet: import('@/components/verdict/FinancialAnalysisPanel').FinancialKillSheet; snapshot?: import('@/components/verdict/FinancialAnalysisPanel').AnchoredSnapshot; query: string; totalCreditsUsed?: number; bullAgents?: FinancialAgentProgress[]; bearAgents?: FinancialAgentProgress[]; mediatorCompilation?: MediatorCompilation }
  | { type: 'strategic'; deliverable: StrategicDeliverable; stressTests: StressTestResult[]; query: string; intensity: StrategicIntensity; totalCreditsUsed?: number; workstreams?: StrategicWorkstream[]; conflicts?: StrategicConflict[]; debateArguments?: StrategicDebateArgument[]; debateRounds?: number; mediatorConsensus?: MediatorConsensus; ghostDeck?: GhostDeckSlide[]; competitorData?: CompetitorData[]; startedAt?: string; completedAt?: string }
  | { type: 'comparison'; comparisonKillSheet: import('@/components/verdict/FinancialAnalysisPanel').ComparisonKillSheet; killSheet: import('@/components/verdict/FinancialAnalysisPanel').FinancialKillSheet; snapshotA?: import('@/components/verdict/FinancialAnalysisPanel').AnchoredSnapshot; snapshotB?: import('@/components/verdict/FinancialAnalysisPanel').AnchoredSnapshot; query: string; bullAgents?: FinancialAgentProgress[]; bearAgents?: FinancialAgentProgress[]; mediatorCompilation?: MediatorCompilation };

export function VerdictDashboardPage() {
  const { user, logout, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Responsive hooks
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isSmallScreen = isMobile || isTablet;

  // Determine if user is authenticated (for feature gating)
  const isAuthenticated = !!user;

  // Promotional banner state
  const [showPromoBanner, setShowPromoBanner] = useState(true);

  // Browser fingerprint for free users
  const { visitorId: fingerprintId, loading: fingerprintLoading } = useFingerprint();

  // Free query limit tracking (for unauthenticated users)
  const [fingerprintChecked, setFingerprintChecked] = useState(false);
  const [_queriesRemaining, setQueriesRemaining] = useState<number>(3);
  const [maxFreeQueries, setMaxFreeQueries] = useState<number>(3);
  const [requiresSignup, setRequiresSignup] = useState(false);
  const [signupCredits, setSignupCredits] = useState<number>(150);
  // Note: _queriesRemaining is tracked for potential future use but UI shows maxFreeQueries

  // Refs for keyboard shortcuts
  const heroInputRef = useRef<ResearchHeroInputHandle>(null);
  // Track if user manually toggled sidebar (to avoid fighting with auto-collapse)
  const userManuallyToggled = useRef(false);

  // UI state
  const [uiState, setUIState] = useState<UIState>('idle');
  const uiStateRef = useRef(uiState);
  uiStateRef.current = uiState;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isSmallScreen);

  // Product tour for new users
  useProductTour({
    onBeforeStart: () => {
      setSidebarCollapsed(false);
      userManuallyToggled.current = true;
    },
  });

  const [sessionMessages, setSessionMessages] = useState<Array<{
    id: string;
    role: 'user' | 'council' | 'system';
    content: string;
    type: 'query' | 'verdict' | 'followup' | 'chat';
    confidence?: 'high' | 'medium' | 'low';
    suggestResearch?: boolean;
    createdAt: string;
  }>>([]);

  // Session management (only used for authenticated users)
  const {
    sessions,
    currentSession,
    isLoading: sessionsLoading,
    isLoadingSession,
    pendingSessionId,
    createSession,
    selectSession,
    addMessage,
    archiveSession,
    clearCurrentSession,
  } = useResearchSessions();

  // Credit system for authenticated users
	const { balance, isUnlimited, isPaidUser: _isPaidUser, packs, fetchPacks, purchaseCredits, hasSufficientCredits, refreshBalance } = useCredits();
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [showCreditPurchase, setShowCreditPurchase] = useState(false);
  const [_pendingQuery, setPendingQuery] = useState<string | null>(null);
  // pendingQuery is stored for potential retry after credit purchase
  void _pendingQuery;

  // Credit consumption toast
  const preAnalysisBalanceRef = useRef<number | null>(null);
  const [creditToast, setCreditToast] = useState<{ creditsUsed: number; newBalance: number } | null>(null);

  // Track selected research intensity for credit estimation
  const defaultIntensity: ResearchIntensity = 'fast';
  const [selectedIntensity, setSelectedIntensity] = useState<ResearchIntensity>(defaultIntensity);

  // logicTreeCollapsed is no longer needed — ProcessBar manages its own state

  // Track if FollowUpChat is expanded to fullscreen
  const [chatExpanded, setChatExpanded] = useState(false);

  // Restored states for session-level financial/strategic reconstruction
  const [restoredFinancialState, setRestoredFinancialState] = useState<FinancialStreamingState | null>(null);
  const [restoredFinancialQuery, setRestoredFinancialQuery] = useState('');
  const [restoredStrategicState, setRestoredStrategicState] = useState<StrategicStreamingState | null>(null);
  const [restoredStrategicQuery, setRestoredStrategicQuery] = useState('');

  // Debate drawer state
  const [debateDrawerMode, setDebateDrawerMode] = useState<DebateDrawerMode>('closed');
  const debateStream = useDebateStream();
  const debateIsActive = debateStream.state.status !== 'idle';
  const openDebateDrawer = useCallback(() => setDebateDrawerMode('open'), []);
  const closeDebateDrawer = useCallback(() => setDebateDrawerMode('closed'), []);
  const minimizeDebateDrawer = useCallback(() => setDebateDrawerMode('minimized'), []);

  // Track the query submitted but not yet streaming (shown during mode detection + session creation)
  const [pendingResearchQuery, setPendingResearchQuery] = useState<string | null>(null);

  // Track completed research results (any mode: general, financial, strategic) for chat-style feed
  const [completedResults, setCompletedResults] = useState<HistoricalResult[]>([]);

  // Track collapsed state for each completed result (by index)
  const [completedCollapsedStates, setCompletedCollapsedStates] = useState<boolean[]>([]);

  // Quote-to-chat: text selected in research results to prefill the chat input
  const [quotedText, setQuotedText] = useState<string | null>(null);
  const resultsContentRef = useRef<HTMLDivElement>(null);

  // Refresh balance and show credit consumption toast
  const refreshBalanceWithToast = useCallback(async () => {
    const prevBalance = preAnalysisBalanceRef.current;
    const result = await refreshBalance();
    if (result && prevBalance !== null && !isUnlimited) {
      const used = prevBalance - result.credit_balance;
      if (used > 0) {
        setCreditToast({ creditsUsed: used, newBalance: result.credit_balance });
      }
    }
    preAnalysisBalanceRef.current = null;
  }, [refreshBalance, isUnlimited]);

  // Financial analysis query - stored separately since financial flow doesn't use researchState
  const [financialQuery, setFinancialQuery] = useState<string>('');

  // Financial analysis using new streaming hook
  const {
    state: financialState,
    startAnalysis: startFinancialAnalysis,
    reset: resetFinancialAnalysis,
  } = useFinancialStream({
    onComplete: () => {
      setUIState('conversation');
      refreshBalanceWithToast();
    },
    onError: (error) => {
      setErrorMessage(error);
      setUIState('idle');
    },
  });

  // Strategic analysis query - stored separately since strategic flow doesn't use researchState
  const [strategicQuery, setStrategicQuery] = useState<string>('');

  // Strategic analysis using new streaming hook
  const {
    state: strategicState,
    startAnalysis: startStrategicAnalysis,
    reset: resetStrategicAnalysis,
  } = useStrategicStream({
    onComplete: () => {
      setUIState('conversation');
      refreshBalanceWithToast();
    },
    onError: (error) => {
      setErrorMessage(error);
      setUIState('idle');
    },
  });

  // Feature showcase demo state
  const [activeDemo, setActiveDemo] = useState<FeatureDemo | null>(null);
  const [demoQuestion, setDemoQuestion] = useState<string>('');
  const [demoLimitReached, setDemoLimitReached] = useState(false);

  // Error state for displaying errors to users
  const [errorMessage, setErrorMessage] = useState<string | null>(null);


  // Research session state (for Logic Tree visualization)
  const {
    session: researchState,
    updateSession: updateResearchState,
    submitAuthenticatedQuery,
    submitFingerprintQuery,
    reset: resetResearch,
    isActive: isResearchActive,
  } = useResearchSession({
    isAuthenticated,
    onComplete: () => {
      // Transition to conversation state — ProcessBar handles collapse internally
      setTimeout(() => {
        setUIState('conversation');
      }, 500);
      refreshBalanceWithToast();
    },
    onError: (error) => {
      console.error('Research error:', error);
    },
  });

  // No redirect for unauthenticated users - they can use free trial mode

  // Packs are lazy-loaded — fetch them when this page mounts (needed for purchase modals)
  useEffect(() => { if (isAuthenticated) fetchPacks(); }, [isAuthenticated, fetchPacks]);

  // Auto-collapse sidebar when research starts, re-expand on idle (desktop only)
  useEffect(() => {
    if (isSmallScreen) return; // Mobile handles its own sidebar
    if (userManuallyToggled.current) return; // Respect manual toggle

		// Collapse sidebar only during active research; re-expand otherwise.
		if (uiState === 'researching') {
      setSidebarCollapsed(true);
    } else {
      setSidebarCollapsed(false);
    }
  }, [uiState, isSmallScreen]);

  // Reset manual toggle flag when returning to idle
  useEffect(() => {
    if (uiState === 'idle') {
      userManuallyToggled.current = false;
    }
  }, [uiState]);

  // Check fingerprint query count for unauthenticated users (sync with landing page limits)
  useEffect(() => {
    if (isAuthenticated || fingerprintLoading || !fingerprintId || fingerprintChecked) return;

    const checkFingerprint = async () => {
      try {
        const status = await api.checkFingerprintStatus(fingerprintId);
        setQueriesRemaining(status.queries_remaining);
        setMaxFreeQueries(status.max_free_queries);
        setRequiresSignup(status.requires_signup);
        setSignupCredits(status.signup_credits);
        setFingerprintChecked(true);
      } catch (err) {
        console.error('Failed to check fingerprint status:', err);
        // Default to permissive values on error
        setQueriesRemaining(3);
        setRequiresSignup(false);
        setFingerprintChecked(true);
      }
    };

    checkFingerprint();
  }, [isAuthenticated, fingerprintId, fingerprintLoading, fingerprintChecked]);

  // Handle initial verdict from free report follow-up navigation
  useEffect(() => {
    const state = location.state as { initialVerdict?: string; originalQuery?: string; sources?: unknown[] } | null;
    if (state?.initialVerdict && sessionMessages.length === 0) {
      // Create a verdict message from the free report
      const verdictMessage = {
        id: `verdict-${Date.now()}`,
        role: 'council' as const,
        content: state.initialVerdict,
        type: 'verdict' as const,
        createdAt: new Date().toISOString(),
      };

      // Optionally include the original query as context
      const messages = state.originalQuery
        ? [
            {
              id: `query-${Date.now()}`,
              role: 'user' as const,
              content: state.originalQuery,
              type: 'query' as const,
              createdAt: new Date().toISOString(),
            },
            verdictMessage,
          ]
        : [verdictMessage];

      setSessionMessages(messages);
      setUIState('conversation');

      // Clear the navigation state to prevent re-adding on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, sessionMessages.length]);


  // Execute research after credit check passes
  const executeResearch = useCallback(async (query: string, intensity: ResearchIntensity = 'fast', mode: ResearchMode = 'auto', files?: File[]) => {
    try {
      setUIState('researching');
      setPendingResearchQuery(query);

      // Resolve effective mode using backend LLM classifier when mode is 'auto'
      // This replaces the old client-side regex patterns which could disagree with
      // the backend's classification, causing visual desync (e.g., backend runs
      // financial pipeline but frontend renders general blueprint)
      let effectiveMode: ResearchMode = mode;
      if (mode === 'auto') {
        try {
          const detection = await api.detectResearchMode(query, mode);
          effectiveMode = detection.effective_mode as ResearchMode;
          console.log(
            `[Mode Detection] "${query.slice(0, 50)}..." -> ${effectiveMode}`,
            `(confidence=${detection.confidence.toFixed(2)})`
          );
        } catch (err) {
          // If mode detection fails, fall through to general research
          console.warn('Mode detection failed, defaulting to general:', err);
          effectiveMode = 'general';
        }
      }

      // Route specialized queries to their dedicated pages BEFORE creating a session
      // (prevents orphan sessions that clutter the sidebar)
      if (effectiveMode === 'financial') {
        setUIState('idle');
        setPendingResearchQuery(null);
        navigate(`/dashboard/stock-analysis?q=${encodeURIComponent(query)}`);
        return;
      }

      if (effectiveMode === 'strategic') {
        setUIState('idle');
        setPendingResearchQuery(null);
        navigate(`/dashboard/strategic?subject=${encodeURIComponent(query)}&autoStart=1`);
        return;
      }

      // Ensure session exists for general research (enables chat persistence & FollowUpChat)
      let sessionId = currentSession?.id;
      if (isAuthenticated) {
        if (!sessionId) {
          const newSession = await createSession(query);
          sessionId = newSession?.id;
        } else {
          await addMessage(sessionId, query, 'query', 'user');
        }
      }

      // General research flow
      if (isAuthenticated) {
        // Upload pending files to the session before starting research
        if (files && files.length > 0 && sessionId) {
          try {
            for (const file of files) {
              await api.uploadSessionFile(sessionId, file);
            }
          } catch (fileError) {
            console.warn('Some session files failed to upload:', fileError);
            // Continue with research even if file upload fails
          }
        }

        // Pass intensity and resolved mode to authenticated query
        await submitAuthenticatedQuery(query, sessionId, intensity, effectiveMode);
      } else {
        // Free users: Use fingerprint-based submission (no session persistence)
        // Free users are always locked to 'fast' intensity
        if (fingerprintId) {
          // Use a dummy turnstile token for dashboard - backend will validate differently
          await submitFingerprintQuery(query, fingerprintId, 'dashboard-free-trial', intensity, effectiveMode);
        } else {
          throw new Error('Unable to verify browser. Please refresh and try again.');
        }
      }
    } catch (error) {
      console.error('Failed to submit research:', error);
      setUIState('idle');
      setPendingResearchQuery(null);
      // Display error message to user
      const errorMsg = error instanceof Error ? error.message : 'An unexpected error occurred';
      setErrorMessage(errorMsg);
    }
  }, [isAuthenticated, currentSession, createSession, addMessage, submitAuthenticatedQuery, submitFingerprintQuery, fingerprintId, startFinancialAnalysis, startStrategicAnalysis]);

  // Handle research submission with credit/quota check
  const handleResearchSubmit = useCallback(async (query: string, intensity: ResearchIntensity = 'fast', mode: ResearchMode = 'auto', files?: File[]) => {
    // For unauthenticated users, check if they have free queries remaining
    if (!isAuthenticated && requiresSignup) {
      // User has exhausted free queries - redirect to signup
      navigate('/signup');
      return;
    }

    // For authenticated paid users, check credits first
    if (isAuthenticated && balance !== null && !isUnlimited) {
      const requiredCredits = INTENSITY_CREDIT_THRESHOLDS[intensity];
      if (!hasSufficientCredits(requiredCredits)) {
        setPendingQuery(query);
        setShowInsufficientCredits(true);
        return;
      }
    }
    // Snapshot balance before analysis for credit consumption toast
    if (balance !== null && !isUnlimited) {
      preAnalysisBalanceRef.current = balance;
    }

    // Execute research with selected intensity, mode, and optional files
    await executeResearch(query, intensity, mode, files);
  }, [isAuthenticated, requiresSignup, navigate, balance, isUnlimited, hasSufficientCredits, executeResearch]);

  // Helper: save the current concluded result (any mode) to completedResults
  const saveCurrentResultToHistory = useCallback(() => {
    if (financialState.phase === 'concluded' && financialState.killSheet) {
      // Check if this was a comparison analysis
      if (financialState.isComparison && financialState.comparisonKillSheet) {
        setCompletedResults(prev => [...prev, {
          type: 'comparison',
          comparisonKillSheet: financialState.comparisonKillSheet!,
          killSheet: financialState.killSheet!,
          snapshotA: financialState.comparisonSnapshotA,
          snapshotB: financialState.comparisonSnapshotB,
          query: financialQuery,
          bullAgents: [...financialState.bullAgents],
          bearAgents: [...financialState.bearAgents],
          mediatorCompilation: financialState.mediatorCompilation,
        }]);
        setCompletedCollapsedStates(prev => [...prev, false]);
        return true;
      }
      setCompletedResults(prev => [...prev, {
        type: 'financial',
        killSheet: financialState.killSheet!,
        snapshot: financialState.hardDataSnapshot,
        query: financialQuery,
        totalCreditsUsed: financialState.totalCreditsUsed,
        bullAgents: [...financialState.bullAgents],
        bearAgents: [...financialState.bearAgents],
        mediatorCompilation: financialState.mediatorCompilation,
      }]);
      setCompletedCollapsedStates(prev => [...prev, false]);
      return true;
    }
    if (strategicState.phase === 'concluded' && strategicState.deliverable) {
      setCompletedResults(prev => [...prev, {
        type: 'strategic',
        deliverable: strategicState.deliverable!,
        stressTests: [...strategicState.stressTestResults],
        query: strategicQuery,
        intensity: strategicState.intensity,
        totalCreditsUsed: strategicState.totalCreditsUsed,
        workstreams: [...strategicState.workstreams],
        conflicts: [...strategicState.conflicts],
        debateArguments: [...strategicState.debateArguments],
        debateRounds: strategicState.debateRounds,
        mediatorConsensus: strategicState.mediatorConsensus,
        ghostDeck: [...strategicState.ghostDeck],
        competitorData: [...strategicState.competitorData],
        startedAt: strategicState.startedAt,
        completedAt: strategicState.completedAt,
      }]);
      setCompletedCollapsedStates(prev => [...prev, false]);
      return true;
    }
    if (researchState.phase === 'concluded' && researchState.reportData) {
      const sessionToSave: ResearchSession = {
        ...researchState,
        debatesPerTask: new Map(researchState.debatesPerTask),
        agentOutputs: new Map(researchState.agentOutputs),
        taskAgentStatuses: new Map(researchState.taskAgentStatuses),
        factCheckProgress: new Map(researchState.factCheckProgress),
        tasks: [...researchState.tasks],
        agents: [...researchState.agents],
        activityFeed: [...researchState.activityFeed],
      };
      setCompletedResults(prev => [...prev, { type: 'general', session: sessionToSave }]);
      setCompletedCollapsedStates(prev => [...prev, false]);
      return true;
    }
    return false;
  }, [financialState, financialQuery, strategicState, strategicQuery, researchState]);

  // Clear pending query once actual research stream begins
  useEffect(() => {
    if (researchState.phase !== 'idle' || financialState.phase !== 'idle' || strategicState.phase !== 'idle') {
      setPendingResearchQuery(null);
    }
  }, [researchState.phase, financialState.phase, strategicState.phase]);

  // When any research concludes, refresh session messages from backend so FollowUpChat
  // retains quick-chat messages that were stored server-side during the session
  useEffect(() => {
    const researchJustConcluded =
      researchState.phase === 'concluded' ||
      financialState.phase === 'concluded' ||
      strategicState.phase === 'concluded';

    if (researchJustConcluded && currentSession?.id) {
      (async () => {
        try {
          const session = await api.getResearchSession(currentSession.id);
          if (session.messages) {
            const mapped = (session.messages as Array<{ id: string; role: string; content: string; message_type: string; created_at: string }>)
              .filter(msg => msg.message_type !== 'verdict' && msg.message_type !== 'query')
              .map(msg => ({
                id: msg.id,
                role: msg.role as 'user' | 'council' | 'system',
                content: msg.content,
                type: msg.message_type as 'query' | 'verdict' | 'followup' | 'chat',
                createdAt: msg.created_at,
              }));
            setSessionMessages(mapped);
          }
        } catch (err) {
          console.warn('Failed to refresh session messages:', err);
        }
      })();
    }
  }, [researchState.phase, financialState.phase, strategicState.phase, currentSession?.id]);

  // Handle "Research Deeper" from follow-up chat (only for authenticated users)
  // Now accepts intensity parameter from FollowUpChat's intensity selector
  const handleResearchDeeper = useCallback(async (query: string, intensity: ResearchIntensity = 'fast', mode: ResearchMode = 'auto') => {
    if (!isAuthenticated) return; // Disabled for free users
    if (!query.trim() || !currentSession) return;

    // Save current result (any mode) to completedResults before starting new research
    saveCurrentResultToHistory();

    // Reset the mode that just concluded so the new research can start fresh
    if (financialState.phase === 'concluded') {
      resetFinancialAnalysis();
      setFinancialQuery('');
    }
    if (strategicState.phase === 'concluded') {
      resetStrategicAnalysis();
      setStrategicQuery('');
    }

    // Use the intensity and mode selected by user in FollowUpChat
    await handleResearchSubmit(query.trim(), intensity, mode);
  }, [isAuthenticated, currentSession, handleResearchSubmit, saveCurrentResultToHistory, financialState.phase, strategicState.phase, resetFinancialAnalysis, resetStrategicAnalysis]);

  // Toggle collapsed state for a completed result
  const toggleCompletedCollapsed = useCallback((index: number) => {
    setCompletedCollapsedStates(prev => {
      const newStates = [...prev];
      newStates[index] = !newStates[index];
      return newStates;
    });
  }, []);

  // Handle "Continue Researching" - saves current result, resets for new query, keeps session
  const handleContinueResearching = useCallback(() => {
    // Save current result to history
    saveCurrentResultToHistory();

    // Reset active states (but NOT completedResults or currentSession)
    resetFinancialAnalysis();
    setFinancialQuery('');
    resetStrategicAnalysis();
    setStrategicQuery('');
    resetResearch();
    setRestoredFinancialState(null);
    setRestoredFinancialQuery('');
    setRestoredStrategicState(null);
    setRestoredStrategicQuery('');
    setErrorMessage(null);
    setUIState('idle');
    // Keep currentSession and completedResults intact
  }, [saveCurrentResultToHistory, resetFinancialAnalysis, resetStrategicAnalysis, resetResearch]);

  // Handle new session (full reset, used from sidebar)
  const handleNewResearch = useCallback(() => {
    resetFinancialAnalysis();
    setFinancialQuery('');
    resetStrategicAnalysis();
    setStrategicQuery('');
    resetResearch();
    setRestoredFinancialState(null);
    setRestoredFinancialQuery('');
    setRestoredStrategicState(null);
    setRestoredStrategicQuery('');
    setSessionMessages([]);
    setCompletedResults([]); // Clear all completed results for fresh session
    setCompletedCollapsedStates([]);
    setErrorMessage(null);
    setActiveDemo(null);
    setDemoQuestion('');
    setUIState('idle');
    setChatExpanded(false);
    clearCurrentSession();
  }, [resetFinancialAnalysis, resetStrategicAnalysis, resetResearch, clearCurrentSession]);

  // Handle selecting a historical session from sidebar (reset state so blueprint restoration runs)
  const handleSelectSession = useCallback((sessionId: string) => {
    // Skip if already viewing this session
    if (currentSession?.id === sessionId) return;

    // Reset active research state so blueprint restoration can run (uiState must be 'idle')
    resetResearch();
    resetFinancialAnalysis();
    setFinancialQuery('');
    resetStrategicAnalysis();
    setStrategicQuery('');
    setCompletedResults([]);
    setCompletedCollapsedStates([]);
    setSessionMessages([]);
    setErrorMessage(null);
    setRestoredFinancialState(null);
    setRestoredFinancialQuery('');
    setRestoredStrategicState(null);
    setRestoredStrategicQuery('');
    setUIState('idle');
    setChatExpanded(false);

    selectSession(sessionId);
  }, [currentSession?.id, selectSession, resetResearch, resetFinancialAnalysis, resetStrategicAnalysis]);

  // Handle switching from Financial Deep Dive to General Research mode
  const handleSwitchToGeneralResearch = useCallback((query: string) => {
    // Reset financial state first
    resetFinancialAnalysis();
    setFinancialQuery('');
    // Execute as general research with 'general' mode to bypass financial detection
    executeResearch(query, selectedIntensity, 'general');
  }, [resetFinancialAnalysis, executeResearch, selectedIntensity]);

  // Handle analyzing a specific ticker — navigate to dedicated Stock Analysis page
  const handleAnalyzeTicker = useCallback((ticker: string) => {
    navigate(`/dashboard/stock-analysis?ticker=${encodeURIComponent(ticker)}`);
  }, [navigate]);

  // Fetch session messages when session changes and transition to conversation state
  useEffect(() => {
    const abortController = new AbortController();

    const fetchMessages = async () => {
      if (!currentSession?.id) {
        setSessionMessages([]);
        return;
      }

      try {
        const session = await api.getResearchSession(currentSession.id);
        if (abortController.signal.aborted) return;
        if (session.messages) {
          // Parse verdict messages to extract displayable content (not raw JSON)
          const mappedMessages = session.messages.map(msg => {
            // For verdict messages, parse and extract just the verdict text for display
            if (msg.message_type === 'verdict') {
              const parsed = parseVerdictContent(msg.content);
              return {
                id: msg.id,
                role: msg.role as 'user' | 'council' | 'system',
                content: parsed.verdict, // Display just the verdict text, not raw JSON
                type: msg.message_type as 'query' | 'verdict' | 'followup' | 'chat',
                createdAt: msg.created_at
              };
            }
            return {
              id: msg.id,
              role: msg.role as 'user' | 'council' | 'system',
              content: msg.content,
              type: msg.message_type as 'query' | 'verdict' | 'followup' | 'chat',
              createdAt: msg.created_at
            };
          });
          setSessionMessages(mappedMessages);

          // If session has messages, transition to conversation state
          // This handles the case when user clicks on an existing session
          if (mappedMessages.length > 0 && uiStateRef.current === 'idle') {
            setUIState('conversation');
            // Extract ALL verdicts from messages to restore complete history
            const verdictMessages = session.messages.filter(m => m.message_type === 'verdict');

            if (verdictMessages.length > 0) {
              // Parse all verdicts
              const allParsedVerdicts = verdictMessages.map(v => parseVerdictContent(v.content));

              // Helper function to convert ParsedVerdict to ResearchSession (General mode)
              const convertToResearchSession = (parsed: ParsedVerdict): ResearchSession => {
                const agentColors = ['#10B981', '#F97316', '#3B82F6', '#8B5CF6', '#EC4899'];

                const tasks: import('@/types').TaskBreakdown[] = parsed.blueprint?.tasks?.map(t => ({
                  name: t.name,
                  description: t.description,
                  execution_mode: t.execution_mode || 'parallel',
                })) || [];

                const uniqueAgentNames = new Set<string>();
                parsed.blueprint?.tasks?.forEach(t => {
                  t.agents?.forEach(a => uniqueAgentNames.add(a.name));
                });
                const agents: import('@/hooks/useResearchSession').ActiveAgent[] =
                  Array.from(uniqueAgentNames).map((name, idx) => ({
                    id: `restored-${idx}`,
                    name,
                    model: name,
                    status: 'idle' as const,
                    color: agentColors[idx % agentColors.length],
                  }));

                const agentOutputs = new Map<string, import('@/hooks/useResearchSession').AgentOutputData>();
                parsed.blueprint?.tasks?.forEach((task, taskIndex) => {
                  task.agents?.forEach((agent, agentIndex) => {
                    const key = `${taskIndex}-${agentIndex}`;
                    agentOutputs.set(key, {
                      taskIndex,
                      agentIndex,
                      agentName: agent.name,
                      model: agent.model,
                      provider: agent.provider,
                      outputPreview: agent.output_preview || '',
                      fullOutput: agent.full_output,
                      latencySeconds: agent.latency_seconds || 0,
                      success: agent.success,
                      webSourcesUsed: agent.web_sources_used || 0,
                    });
                  });
                });

                const factCheckProgress = new Map<number, import('@/hooks/useResearchSession').FactCheckProgress>();
                parsed.blueprint?.tasks?.forEach((task, taskIndex) => {
                  factCheckProgress.set(taskIndex, {
                    taskIndex,
                    isActive: false,
                    isComplete: true,
                    model: task.fact_check?.model,
                    provider: task.fact_check?.provider,
                    confidenceScore: task.confidence_score,
                    isValidated: task.fact_check?.is_validated,
                    keyFindings: task.fact_check?.key_findings || [],
                    discrepancies: task.fact_check?.discrepancies || [],
                    needsDebate: task.debate_occurred,
                  });
                });

                const debatesPerTask = new Map<number, import('@/hooks/useResearchSession').DebateState>();
                parsed.blueprint?.tasks?.forEach((task, taskIndex) => {
                  if (task.debate_occurred && task.debate) {
                    debatesPerTask.set(taskIndex, {
                      isActive: false,
                      currentRound: task.debate.rounds_completed,
                      maxRounds: task.debate.rounds_completed,
                      speakingAgent: null,
                      mediatorOpening: null,
                      arguments: [],
                      consensusProgress: task.debate.agreement_score,
                      discrepancies: task.debate.resolved_discrepancies || [],
                      finalConsensus: task.debate.final_consensus || null,
                      taskIndex,
                      debateMode: null,
                      challengesTested: task.debate.challenges_tested || [],
                      challengesHeld: task.debate.challenges_held || [],
                      watchItems: task.debate.watch_items || [],
                    });
                  }
                });

                const taskResults = parsed.blueprint?.tasks?.map(t => ({
                  taskName: t.name,
                  taskDescription: t.description,
                  agentOutputs: t.agents?.map(a => ({
                    agentName: a.name,
                    modelName: a.model,
                    output: a.output_preview || '',
                  })) || [],
                  aggregatedOutput: t.aggregated_output || '',
                  confidenceScore: t.confidence_score,
                })) || [];

                const sources = parsed.blueprint?.tasks?.flatMap(t =>
                  t.web_sources?.map(s => ({ url: s.url, title: s.title })) || []
                ) || [];

                return {
                  query: parsed.blueprint?.topic || '',
                  phase: 'concluded' as const,
                  tasks,
                  currentTaskIndex: tasks.length - 1,
                  totalTasks: tasks.length,
                  agents,
                  reportId: null,
                  historyId: null,
                  email: null,
                  reportsRemaining: null,
                  planningTimeMs: null,
                  error: null,
                  isNewLead: false,
                  reportData: {
                    verdict: parsed.verdict,
                    taskResults,
                    sources,
                    durationSeconds: parsed.blueprint?.total_latency_seconds,
                  },
                  isSealed: false,
                  sealedHeadline: null,
                  debate: null,
                  debatesPerTask,
                  agentOutputs,
                  taskAgentStatuses: new Map(),
                  factCheckProgress,
                  isFinalAggregating: false,
                  requestedIntensity: null,
                  effectiveIntensity: null,
                  creditMultiplier: null,
                  requestedMode: null,
                  effectiveMode: null,
                  activityFeed: [],
                  totalSourcesFound: parsed.blueprint?.total_web_sources || 0,
                  researchStartTime: null,
                  totalCreditsUsed: parsed.total_credits_used || 0,
                };
              };

              // Separate verdicts by mode — build results synchronously, defer strategic fetches
              const restoredResults: HistoricalResult[] = [];
              // Track indices where strategic results need async fetch
              const strategicFetchSlots: { index: number; resultId: number }[] = [];

              for (const parsed of allParsedVerdicts) {
                const mode = parsed.blueprint?.mode;

                if (mode === 'financial') {
                  // Restore financial state from enriched verdict data
                  const bp = parsed.blueprint;
                  const bpRecord = (bp || {}) as Record<string, unknown>;
                  const hasAgentData = hasFullFinancialState(bpRecord);

                  if (hasAgentData) {
                    const restoredState = restoreFinancialState({
                      ...bpRecord,
                      killSheet: parsed.kill_sheet,
                      hardDataSnapshot: parsed.anchored_snapshot,
                    });

                    restoredResults.push({
                      type: 'financial',
                      killSheet: (restoredState.killSheet || parsed.kill_sheet) as any,
                      snapshot: restoredState.hardDataSnapshot as any,
                      query: bp?.topic || '',
                      totalCreditsUsed: parsed.total_credits_used,
                      bullAgents: restoredState.bullAgents,
                      bearAgents: restoredState.bearAgents,
                      mediatorCompilation: restoredState.mediatorCompilation,
                    });
                  } else {
                    // Legacy financial verdict (no agent data)
                    restoredResults.push({
                      type: 'financial',
                      killSheet: parsed.kill_sheet as any,
                      snapshot: parsed.anchored_snapshot as any,
                      query: bp?.topic || '',
                      totalCreditsUsed: parsed.total_credits_used,
                    });
                  }
                } else if (mode === 'strategic' && parsed.blueprint?.strategic_result_id) {
                  // Reserve a slot; fill it async below (non-blocking)
                  const slotIndex = restoredResults.length;
                  restoredResults.push(null as unknown as HistoricalResult); // placeholder
                  strategicFetchSlots.push({ index: slotIndex, resultId: parsed.blueprint.strategic_result_id });
                } else {
                  // General mode (default)
                  const restoredSession = convertToResearchSession(parsed);
                  restoredResults.push({
                    type: 'general' as const,
                    session: restoredSession,
                  });
                }
              }

              // Fetch strategic results concurrently (non-blocking)
              if (strategicFetchSlots.length > 0) {
                const token = localStorage.getItem('access_token');
                const authHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
                if (token) authHeaders['Authorization'] = `Bearer ${token}`;

                const fetches = strategicFetchSlots.map(async ({ index, resultId }) => {
                  try {
                    const res = await fetch(
                      `${API_BASE_URL}/api/v1/strategic/result/${resultId}`,
                      { headers: authHeaders, signal: abortController.signal }
                    );
                    if (res.ok) {
                      const data = await res.json();
                      if (data.full_state) {
                        const restoredState = restoreStrategicState(data.full_state as Record<string, unknown>);
                        restoredResults[index] = {
                          type: 'strategic',
                          deliverable: restoredState.deliverable!,
                          stressTests: restoredState.stressTestResults,
                          query: restoredState.topic,
                          intensity: restoredState.intensity,
                          totalCreditsUsed: restoredState.totalCreditsUsed,
                          workstreams: restoredState.workstreams,
                          conflicts: restoredState.conflicts,
                          debateArguments: restoredState.debateArguments,
                          debateRounds: restoredState.debateRounds,
                          mediatorConsensus: restoredState.mediatorConsensus,
                          ghostDeck: restoredState.ghostDeck,
                          competitorData: restoredState.competitorData,
                          startedAt: restoredState.startedAt,
                          completedAt: restoredState.completedAt,
                        };
                      }
                    }
                  } catch {
                    // Silent fail — strategic result may have been deleted or request aborted
                  }
                });
                await Promise.all(fetches);
              }

              // Filter out any failed strategic slots (still null placeholders)
              const validResults = restoredResults.filter(Boolean);

              if (abortController.signal.aborted) return;

              // Set completed results (all except the last)
              if (validResults.length > 0) {
                // Find the last result to use as active state
                const lastResult = validResults[validResults.length - 1];
                const completed = validResults.length > 1 ? validResults.slice(0, -1) : [];
                setCompletedResults(completed);
                setCompletedCollapsedStates(new Array(completed.length).fill(false));

                // Set the active state based on the last result's type
                if (lastResult.type === 'general') {
                  const session = lastResult.session;
                  updateResearchState({
                    query: currentSession.title || session.query,
                    phase: 'concluded',
                    tasks: session.tasks,
                    totalTasks: session.totalTasks,
                    agents: session.agents,
                    agentOutputs: session.agentOutputs,
                    factCheckProgress: session.factCheckProgress,
                    debatesPerTask: session.debatesPerTask,
                    reportData: session.reportData,
                  });
                } else if (lastResult.type === 'financial' && lastResult.killSheet) {
                  // For financial, restore into the financial stream state
                  const lastBp = allParsedVerdicts[allParsedVerdicts.length - 1].blueprint;
                  const lastBpRecord = (lastBp || {}) as Record<string, unknown>;
                  if (hasFullFinancialState(lastBpRecord)) {
                    const restoredState = restoreFinancialState({
                      ...lastBpRecord,
                      killSheet: lastResult.killSheet,
                      hardDataSnapshot: lastResult.snapshot,
                    });
                    setRestoredFinancialState(restoredState);
                    setRestoredFinancialQuery(lastResult.query);
                  }
                } else if (lastResult.type === 'strategic' && lastResult.deliverable) {
                  setRestoredStrategicState({
                    phase: 'concluded',
                    topic: lastResult.query,
                    intensity: lastResult.intensity,
                    workstreamCount: lastResult.workstreams?.length || 0,
                    totalAgents: 0,
                    estimatedSeconds: 0,
                    workstreams: lastResult.workstreams || [],
                    conflicts: lastResult.conflicts || [],
                    stressTestResults: lastResult.stressTests,
                    debateArguments: lastResult.debateArguments || [],
                    debateRound: lastResult.debateRounds || 0,
                    debateRounds: lastResult.debateRounds || 0,
                    mediatorConsensus: lastResult.mediatorConsensus,
                    ghostDeck: lastResult.ghostDeck || [],
                    deliverable: lastResult.deliverable,
                    competitorData: lastResult.competitorData || [],
                    startedAt: lastResult.startedAt,
                    completedAt: lastResult.completedAt,
                    totalCreditsUsed: lastResult.totalCreditsUsed,
                  });
                  setRestoredStrategicQuery(lastResult.query);
                }
              } else {
                setCompletedResults([]);
                setCompletedCollapsedStates([]);
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch session messages:', error);
      }
    };

    fetchMessages();
    return () => abortController.abort();
    // Note: uiState is read inside via uiStateRef to avoid re-triggering on state change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession?.id]);

  // Auto-collapse sidebar on mobile/tablet
  useEffect(() => {
    if (isSmallScreen) {
      setSidebarCollapsed(true);
    }
  }, [isSmallScreen]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input (except for Escape)
      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

      // Escape - Reset to idle state (from any state)
      if (e.key === 'Escape') {
        if (uiState !== 'idle') {
          setUIState('idle');
          handleNewResearch();
        }
        return;
      }

      // Skip other shortcuts if typing
      if (isTyping) return;

      // Ctrl/Cmd + K or "/" - Focus search input (when in idle state)
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || e.key === '/') {
        if (uiState === 'idle') {
          e.preventDefault();
          heroInputRef.current?.focus();
        }
        return;
      }

      // Ctrl/Cmd + N - New research session
      if (e.key === 'n' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleNewResearch();
        return;
      }

      // Ctrl/Cmd + \ - Toggle sidebar (backslash)
      if (e.key === '\\' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        userManuallyToggled.current = true;
        setSidebarCollapsed(prev => !prev);
        return;
      }

      // Ctrl+Shift+D - Toggle debate drawer
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setDebateDrawerMode((prev) => {
          if (prev === 'open') {
            return debateStream.state.status !== 'idle' ? 'minimized' : 'closed';
          }
          return 'open';
        });
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [uiState, handleNewResearch, debateStream.state.status]);

  // Loading state
  if (authLoading || (!isAuthenticated && fingerprintLoading)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-verdict-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      {/* Sidebar: unified Sidebar with page-specific content as children */}
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => { userManuallyToggled.current = true; setSidebarCollapsed(!sidebarCollapsed); }}
      >
        {isAuthenticated ? (
          <ResearchSidebarContent
            sessions={sessions}
            currentSessionId={currentSession?.id}
            onSelectSession={handleSelectSession}
            onNewSession={handleNewResearch}
            onArchiveSession={archiveSession}
            isLoading={sessionsLoading}
            pendingSessionId={pendingSessionId}
            isLoadingSession={isLoadingSession}
          />
        ) : (
          <FreeUserSidebarContent
            onNewSession={handleNewResearch}
            hideSignupCTA={requiresSignup}
          />
        )}
      </Sidebar>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-h-0 overflow-auto">
        {/* Header */}
        <header className="flex items-center justify-between p-3 sm:p-4 border-b border-border-subtle pt-16 md:pt-3">
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Desktop-only expand button when sidebar is collapsed */}
            {sidebarCollapsed && !isSmallScreen && (
              <Tooltip content="Open sidebar" position="bottom">
                <button
                  onClick={() => { userManuallyToggled.current = true; setSidebarCollapsed(!sidebarCollapsed); }}
                  className="p-2 rounded-lg hover:bg-surface-light transition-colors"
                  aria-label="Open sidebar"
                >
                  <Menu className="w-5 h-5 text-text-secondary" />
                </button>
              </Tooltip>
            )}
          </div>

          {/* Header Actions - Responsive sizing */}
          <div className="flex items-center gap-2 sm:gap-3">
            {isAuthenticated && user ? (
              <UserMenu
                user={user}
                onLogout={logout}
                creditBalance={balance}
                isUnlimited={isUnlimited}
              />
            ) : !isAuthenticated ? (
              <>
                {/* Log In button - always visible */}
                <Link
                  to="/login"
                  className="px-3 sm:px-4 py-1.5 text-xs sm:text-sm text-text-primary hover:text-text-primary border border-border hover:border-border-light transition-colors rounded-lg"
                >
                  Log In
                </Link>
                <Link
                  to="/signup"
                  className="px-3 sm:px-4 py-1.5 text-xs sm:text-sm bg-verdict-primary/10 border border-verdict-primary/30 text-verdict-primary hover:bg-verdict-primary/20 transition-colors rounded-lg"
                >
                  Sign Up
                </Link>
              </>
            ) : null}
          </div>
        </header>

        {/* Founder Account Banner */}
        {showPromoBanner && user?.is_founder && (
          <div className="px-4 py-2.5 flex items-center justify-between gap-4 text-sm bg-gradient-to-r from-verdict-primary/20 to-verdict-primary/5 border-b border-verdict-primary/20">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="relative flex-shrink-0">
                <Crown className="w-4 h-4 text-verdict-primary" />
              </div>
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-verdict-primary text-black flex-shrink-0">
                FOUNDER
              </span>
              <span className="text-text-primary truncate">
                <span className="font-medium text-text-primary">Full access</span> to all premium AI models. Credits never expire.
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => navigate('/dashboard/plans')}
                className="px-3 py-1 text-xs font-medium bg-verdict-primary/20 border border-verdict-primary/30 text-verdict-primary hover:bg-verdict-primary/30 rounded-md transition-colors flex items-center gap-1.5"
              >
                <Sparkles className="w-3 h-3" />
                Get More Credits
              </button>
              <button
                onClick={() => setShowPromoBanner(false)}
                className="p-1 text-text-muted hover:text-text-primary transition-colors"
                aria-label="Dismiss banner"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Content based on UI state */}
        <div className="flex-1 flex flex-col relative overflow-x-hidden">
          {/* State 0: Idle - Hero input centered (or signup CTA if free queries exhausted) */}
          {uiState === 'idle' && (
            <div className="flex-1 flex flex-col items-center pt-[12vh] sm:pt-[15vh] p-3 sm:p-6 relative">
              {/* Subtle radial glow to draw eye to center */}
              <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center 40%, rgba(255,255,255,0.02) 0%, transparent 70%)' }} />
              {/* Show signup CTA if unauthenticated user has exhausted free queries */}
              {!isAuthenticated && requiresSignup ? (
                <div className="w-full max-w-xl">
                  <div className="relative">
                    {/* Glow effect */}
                    <div className="absolute -inset-1 rounded-2xl blur-xl bg-gradient-to-r from-verdict-primary/0 via-verdict-primary/40 to-verdict-primary/0 opacity-40" />

                    <div className="relative bg-surface border-2 border-verdict-primary/30 rounded-2xl shadow-2xl shadow-black/50 p-6 sm:p-8">
                      <h3 className="text-xl sm:text-2xl font-semibold text-text-primary mb-3">
                        You've used all {maxFreeQueries} free queries
                      </h3>
                      <p className="text-text-secondary text-sm sm:text-base mb-6 leading-relaxed">
                        Sign up now to get <span className="text-verdict-primary font-semibold">{signupCredits} free credits</span> and
                        unlock access to better AI models for more accurate research.
                      </p>
                      <button
                        onClick={() => navigate('/signup')}
                        className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-xl bg-verdict-primary/20 hover:bg-verdict-primary/30 border border-verdict-primary/50 text-verdict-primary font-medium transition-all duration-300 text-base"
                      >
                        <UserPlus className="w-4 h-4" />
                        Sign Up for Free
                      </button>
                      <p className="text-text-muted text-xs mt-4 text-center">
                        Already have an account?{' '}
                        <button
                          onClick={() => navigate('/login')}
                          className="text-verdict-primary hover:underline"
                        >
                          Log in
                        </button>
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {errorMessage && (
                    <Alert
                      variant="error"
                      className="mb-6 max-w-2xl mx-auto"
                      onClose={() => setErrorMessage(null)}
                    >
                      {errorMessage}
                    </Alert>
                  )}
                  <ResearchHeroInput
                    ref={heroInputRef}
                    onSubmit={handleResearchSubmit}
                    isSubmitting={isResearchActive}
                    isFreeUser={!isAuthenticated}
                    creditBalance={balance}
                    isUnlimited={isUnlimited}
                    isAuthenticated={isAuthenticated}
                    defaultIntensity={defaultIntensity}
                    onIntensityChange={setSelectedIntensity}
                  />

                  {/* Feature Showcase or Demo Panel */}
                  {activeDemo ? (
                    <div className="w-full max-w-2xl mx-auto mt-6 animate-fadeIn">
                      {demoLimitReached ? (
                        <div className="h-[450px] flex flex-col items-center justify-center bg-surface/50 rounded-2xl border border-border p-8">
                          <UserPlus className="w-16 h-16 text-verdict-primary mb-4" />
                          <h3 className="text-xl font-semibold text-text-primary mb-2">
                            You've seen all 3 free demos!
                          </h3>
                          <p className="text-text-secondary text-center mb-6 max-w-md">
                            Sign up for a free account to continue exploring our AI research features with 150 free credits.
                          </p>
                          <Link
                            to="/signup"
                            className="px-6 py-3 bg-verdict-primary hover:bg-verdict-primary/90 text-white rounded-lg font-medium transition-colors"
                          >
                            Sign up free
                          </Link>
                        </div>
                      ) : (
                        <div className="h-[450px]">
                          {activeDemo === 'debate' && (
                            <DebateDemo
                              question={demoQuestion}
                              onClose={() => { setActiveDemo(null); setDemoQuestion(''); }}
                              className="h-full"
                              fingerprintId={fingerprintId ?? undefined}
                              onLimitReached={() => setDemoLimitReached(true)}
                            />
                          )}
                          {activeDemo === 'factCheck' && (
                            <FactCheckDemo
                              question={demoQuestion}
                              onClose={() => { setActiveDemo(null); setDemoQuestion(''); }}
                              className="h-full"
                              fingerprintId={fingerprintId ?? undefined}
                              onLimitReached={() => setDemoLimitReached(true)}
                            />
                          )}
                        </div>
                      )}
                      <div className="mt-4 text-center">
                        <button
                          onClick={() => { setActiveDemo(null); setDemoQuestion(''); }}
                          className="text-sm text-text-secondary hover:text-text-primary transition-colors"
                        >
                          <span className="flex items-center gap-2 justify-center">
                            <X className="w-4 h-4" />
                            Close demo
                          </span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full max-w-2xl mx-auto mt-6">
                      <FeatureShowcase
                        onFeatureClick={demoLimitReached ? undefined : (feature: FeatureDemo, question: string, mode?: 'general' | 'financial' | 'strategic') => {
                          if (feature === 'bestAnswer') {
                            if (mode) {
                              // Chip knows its mode — auto-submit, skip LLM detection
                              handleResearchSubmit(question, 'fast', mode as ResearchMode);
                            } else {
                              // Fallback — fill input
                              heroInputRef.current?.setQuery(question);
                              heroInputRef.current?.focus();
                            }
                          } else {
                            setActiveDemo(feature);
                            setDemoQuestion(question);
                          }
                        }}
                        isSubmitting={isResearchActive}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* State 1 & 2: Researching / Conversation - Logic Tree or Financial Panel */}
          {(uiState === 'researching' || uiState === 'conversation') && (
            <div className="flex-1 flex flex-col p-3 sm:p-6 animate-fadeIn">
              {/* Research query breadcrumb — anchors user during loading */}
              {uiState === 'researching' && (pendingResearchQuery || researchState.query || financialQuery || strategicQuery) && (
                <div className="w-full max-w-4xl mx-auto mb-3 flex items-center gap-2 text-xs text-text-muted">
                  <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
                  <span className="truncate">
                    Researching: <span className="text-text-secondary">{pendingResearchQuery || researchState.query || financialQuery || strategicQuery}</span>
                  </span>
                </div>
              )}
              <div className={cn(
                "w-full mx-auto flex flex-col flex-1",
                // Responsive max-width
                isSmallScreen ? "max-w-full" : "max-w-4xl"
              )}>
                {/* Completed Results Feed + Active Research — hidden when chat is expanded */}
                {!chatExpanded && (
                  <div ref={resultsContentRef}>
                    <QuotePopover containerRef={resultsContentRef} onQuote={setQuotedText} />
                    {/* Completed Results Feed - always visible above active research */}
                    {completedResults.length > 0 && (
                      <div className="space-y-3 mb-3">
                        {completedResults.map((result, index) => (
                          <div
                            key={`completed-${index}`}
                          >
                            {result.type === 'general' && (
                              <GeneralResultCard
                                session={result.session}
                                query={result.session.query}
                                onNewResearch={handleNewResearch}
                                isCollapsed={completedCollapsedStates[index] ?? false}
                                onToggleCollapse={() => toggleCompletedCollapsed(index)}
                              />
                            )}
                            {result.type === 'financial' && (
                              <FinancialResultCard
                                killSheet={result.killSheet}
                                anchoredSnapshot={result.snapshot}
                                query={result.query}
                                isCollapsed={completedCollapsedStates[index] ?? false}
                                onToggleCollapse={() => toggleCompletedCollapsed(index)}
                                bullAgents={result.bullAgents}
                                bearAgents={result.bearAgents}
                                mediatorCompilation={result.mediatorCompilation}
                              />
                            )}
                            {result.type === 'comparison' && (
                              <ComparisonResultCard
                                comparisonKillSheet={result.comparisonKillSheet}
                                killSheet={result.killSheet}
                                snapshotA={result.snapshotA}
                                snapshotB={result.snapshotB}
                                query={result.query}
                                isCollapsed={completedCollapsedStates[index] ?? false}
                                onToggleCollapse={() => toggleCompletedCollapsed(index)}
                                bullAgents={result.bullAgents}
                                bearAgents={result.bearAgents}
                                mediatorCompilation={result.mediatorCompilation}
                              />
                            )}
                            {result.type === 'strategic' && result.deliverable && (
                              <StrategicResultCard
                                deliverable={result.deliverable}
                                stressTestResults={result.stressTests}
                                topic={result.query}
                                intensity={result.intensity}
                                totalCreditsUsed={result.totalCreditsUsed}
                                isCollapsed={completedCollapsedStates[index] ?? false}
                                onToggleCollapse={() => toggleCompletedCollapsed(index)}
                                workstreams={result.workstreams}
                                conflicts={result.conflicts}
                                debateArguments={result.debateArguments}
                                debateRounds={result.debateRounds}
                                mediatorConsensus={result.mediatorConsensus}
                                ghostDeck={result.ghostDeck}
                                competitorData={result.competitorData}
                                query={result.query}
                                startedAt={result.startedAt}
                                completedAt={result.completedAt}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Active Research - mode-exclusive rendering with ProcessBar on completion */}
                    {strategicState.phase !== 'idle' ? (
                      strategicState.phase === 'concluded' && strategicState.deliverable ? (
                        <div className="flex-shrink-0 space-y-4 overflow-y-auto">
                          <ProcessBar metadata={getStrategicProcessMetadata(strategicState)} animateCollapse>
                            <StrategicLogicTree
                              state={strategicState}
                              query={strategicQuery}
                              className="w-full"
                            />
                          </ProcessBar>
                          <StrategicDecisionSheet
                            deliverable={strategicState.deliverable}
                            stressTestResults={strategicState.stressTestResults}
                            topic={strategicState.topic}
                            intensity={strategicState.intensity}
                            workstreams={strategicState.workstreams}
                            conflicts={strategicState.conflicts}
                            debateArguments={strategicState.debateArguments}
                            debateRounds={strategicState.debateRounds}
                            mediatorConsensus={strategicState.mediatorConsensus}
                            ghostDeck={strategicState.ghostDeck}
                            competitorData={strategicState.competitorData}
                            startedAt={strategicState.startedAt}
                            completedAt={strategicState.completedAt}
                            onNewResearch={handleContinueResearching}
                          />
                        </div>
                      ) : (
                        <StrategicLogicTree
                          state={strategicState}
                          query={strategicQuery}
                          className="w-full h-full flex-1"
                          onNewResearch={handleContinueResearching}
                        />
                      )
                    ) : financialState.phase !== 'idle' ? (
                      financialState.phase === 'concluded' && financialState.killSheet ? (
                        <div className="flex-shrink-0 space-y-4 overflow-y-auto">
                          <ProcessBar metadata={getFinancialProcessMetadata(financialState)} animateCollapse>
                            <FinancialLogicTree
                              state={financialState}
                              query={financialQuery}
                              className="w-full"
                            />
                          </ProcessBar>
                          <FinancialDecisionSheet
                            killSheet={financialState.killSheet}
                            anchoredSnapshot={financialState.hardDataSnapshot}
                            convergenceTrajectory={financialState.convergenceTrajectory}
                            initialDivergence={financialState.initialDivergence}
                            echoChamberedDetected={financialState.echoChamberedDetected}
                            query={financialQuery}
                          />
                        </div>
                      ) : (
                        <FinancialLogicTree
                          state={financialState}
                          query={financialQuery}
                          className="w-full h-full flex-1"
                          onNewResearch={handleContinueResearching}
                          onSwitchToGeneralResearch={handleSwitchToGeneralResearch}
                          onAnalyzeTicker={handleAnalyzeTicker}
                        />
                      )
                    ) : researchState.phase !== 'idle' ? (
                      <div className={cn(
                        "transition-all duration-500",
                        uiState === 'conversation' ? "flex-shrink-0" : "flex-1 min-h-0"
                      )}>
                        {researchState.phase === 'concluded' && researchState.reportData ? (
                          <div className="space-y-4">
                            <ProcessBar metadata={getGeneralProcessMetadata(researchState)} animateCollapse>
                              <LogicTree
                                session={researchState}
                                className="w-full h-full"
                              />
                            </ProcessBar>
                            <GeneralDecisionSheet
                              session={researchState}
                              query={researchState.query}
                              onNewResearch={handleContinueResearching}
                            />
                          </div>
                        ) : (
                          <LogicTree
                            session={researchState}
                            className="w-full h-full"
                            onNewResearch={handleContinueResearching}
                          />
                        )}
                      </div>
                    ) : restoredFinancialState && restoredFinancialState.killSheet ? (
                      /* Restored financial session from history */
                      <div className="flex-shrink-0 space-y-4 overflow-y-auto">
                        <ProcessBar metadata={getFinancialProcessMetadata(restoredFinancialState)} defaultExpanded={false}>
                          <FinancialLogicTree
                            state={restoredFinancialState}
                            query={restoredFinancialQuery}
                            className="w-full"
                          />
                        </ProcessBar>
                        <FinancialDecisionSheet
                          killSheet={restoredFinancialState.killSheet}
                          anchoredSnapshot={restoredFinancialState.hardDataSnapshot}
                          convergenceTrajectory={restoredFinancialState.convergenceTrajectory}
                          initialDivergence={restoredFinancialState.initialDivergence}
                          echoChamberedDetected={restoredFinancialState.echoChamberedDetected}
                          query={restoredFinancialQuery}
                        />
                      </div>
                    ) : restoredStrategicState && restoredStrategicState.deliverable ? (
                      /* Restored strategic session from history */
                      <div className="flex-shrink-0 space-y-4 overflow-y-auto">
                        <ProcessBar metadata={getStrategicProcessMetadata(restoredStrategicState)} defaultExpanded={false}>
                          <StrategicLogicTree
                            state={restoredStrategicState}
                            query={restoredStrategicQuery}
                            className="w-full"
                          />
                        </ProcessBar>
                        <StrategicDecisionSheet
                          deliverable={restoredStrategicState.deliverable}
                          stressTestResults={restoredStrategicState.stressTestResults}
                          topic={restoredStrategicState.topic}
                          intensity={restoredStrategicState.intensity}
                          workstreams={restoredStrategicState.workstreams}
                          conflicts={restoredStrategicState.conflicts}
                          debateArguments={restoredStrategicState.debateArguments}
                          debateRounds={restoredStrategicState.debateRounds}
                          mediatorConsensus={restoredStrategicState.mediatorConsensus}
                          ghostDeck={restoredStrategicState.ghostDeck}
                          competitorData={restoredStrategicState.competitorData}
                          startedAt={restoredStrategicState.startedAt}
                          completedAt={restoredStrategicState.completedAt}
                          onNewResearch={handleContinueResearching}
                        />
                      </div>
                    ) : uiState === 'researching' ? (
                      /* Skeleton loading while mode detection + session creation are in progress */
                      <div className="flex-1 flex flex-col items-center justify-center gap-6">
                        <div className="w-full max-w-md space-y-4">
                          <div className="flex items-center gap-3 mb-6">
                            <Loader2 className="w-5 h-5 text-verdict-primary animate-spin" />
                            <span className="text-sm text-text-secondary">Preparing your research...</span>
                          </div>
                          {/* Skeleton blocks mimicking blueprint structure */}
                          <div className="space-y-3">
                            <div className="h-4 bg-surface-light rounded animate-pulse w-3/4" />
                            <div className="h-4 bg-surface-light rounded animate-pulse w-full" />
                            <div className="h-4 bg-surface-light rounded animate-pulse w-5/6" />
                          </div>
                          <div className="space-y-3 mt-6">
                            <div className="h-3 bg-surface-light rounded animate-pulse w-2/3" />
                            <div className="h-3 bg-surface-light rounded animate-pulse w-4/5" />
                            <div className="h-3 bg-surface-light rounded animate-pulse w-1/2" />
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}

                {/* FollowUpChat - shown after ANY mode concludes for authenticated users */}
                {uiState === 'conversation' && isAuthenticated && currentSession && (
                  <div className={cn(
                    "animate-slideUp",
                    chatExpanded
                      ? "flex-1"
                      : uiState === 'conversation'
                        ? "flex-1"
                        : isSmallScreen
                          ? "max-h-[200px] flex-shrink-0"
                          : "max-h-[300px] flex-shrink-0"
                  )}>
                    <FollowUpChat
                      sessionId={currentSession.id}
                      initialMessages={sessionMessages}
                      previousVerdict={
                        researchState.reportData?.verdict
                        || financialState.killSheet?.kill_sheet_reason
                        || strategicState.deliverable?.killSheet?.recommendation
                        || (restoredFinancialState?.killSheet as any)?.kill_sheet_reason
                        || restoredStrategicState?.deliverable?.killSheet?.recommendation
                        || researchState.query
                      }
                      onResearchDeeper={handleResearchDeeper}
                      isResearching={isResearchActive}
                      className="h-full"
                      creditBalance={balance}
                      isUnlimited={isUnlimited}
                      quotedText={quotedText}
                      onQuoteClear={() => setQuotedText(null)}
                      userTier={
                        user?.is_master_user
                          ? 'master'
                          : balance !== null && (balance as any)?.is_paid_user
                            ? 'standard'
                            : 'trial'
                      }
                      isExpanded={chatExpanded}
                      onToggleExpand={() => setChatExpanded(!chatExpanded)}
                    />
                  </div>
                )}

                {/* Free User CTA - shown after ANY mode concludes for unauthenticated users */}
                {uiState === 'conversation' && !isAuthenticated && (
                  <div className="flex-shrink-0 animate-slideUp mt-3 sm:mt-4">
                    <div className="bg-gradient-to-r from-verdict-primary/5 to-verdict-primary/10 border border-verdict-primary/20 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-verdict-primary/20 flex items-center justify-center flex-shrink-0">
                          <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-verdict-primary" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-text-primary font-semibold text-sm sm:text-base">Want to dig deeper?</h3>
                          <p className="text-xs sm:text-sm text-text-secondary truncate sm:whitespace-normal">Sign up to unlock follow-up questions & research history</p>
                        </div>
                      </div>
                      <div className={cn(
                        "mt-3 sm:mt-4",
                        isSmallScreen ? "flex flex-col gap-2" : "flex items-center gap-3"
                      )}>
                        <Link
                          to="/signup"
                          className={cn(
                            "flex items-center justify-center gap-2 bg-verdict-primary text-black font-medium rounded-lg hover:bg-verdict-primary/90 transition-colors",
                            isSmallScreen ? "px-4 py-2.5 text-sm w-full" : "px-5 py-2.5"
                          )}
                        >
                          <UserPlus className="w-4 h-4" />
                          Create Free Account
                        </Link>
                        <button
                          onClick={handleContinueResearching}
                          className={cn(
                            "text-text-secondary hover:text-text-primary transition-colors",
                            isSmallScreen ? "px-4 py-2 text-sm" : "px-4 py-2.5"
                          )}
                        >
                          Try another query
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Insufficient Credits Dialog */}
      <InsufficientCreditsDialog
        isOpen={showInsufficientCredits}
        onClose={() => setShowInsufficientCredits(false)}
        currentBalance={balance ?? 0}
        requiredCredits={INTENSITY_CREDIT_THRESHOLDS[selectedIntensity]}
        queryType="standard_research"
        packs={packs}
        onPurchase={purchaseCredits}
      />

      {/* Credit Purchase Modal - accessible from sidebar */}
      <CreditPurchaseModal
        isOpen={showCreditPurchase}
        onClose={() => setShowCreditPurchase(false)}
        currentBalance={balance ?? 0}
        packs={packs}
        onPurchase={purchaseCredits}
      />

      {/* Credit Consumed Toast */}
      {creditToast && (
        <CreditConsumedToast
          creditsUsed={creditToast.creditsUsed}
          newBalance={creditToast.newBalance}
          onDismiss={() => setCreditToast(null)}
        />
      )}

      {/* Debate Drawer */}
      <DebateDrawer
        drawerMode={debateDrawerMode}
        onClose={closeDebateDrawer}
        onMinimize={minimizeDebateDrawer}
        onOpen={openDebateDrawer}
        debateStream={debateStream}
      />

      {/* Debate trigger: show pill when active + drawer not open, FAB otherwise */}
      {debateDrawerMode !== 'open' && debateIsActive ? (
        <DebateMinimizedPill state={debateStream.state} onClick={openDebateDrawer} />
      ) : debateDrawerMode !== 'open' ? (
        <DebateTriggerFAB onClick={openDebateDrawer} />
      ) : null}
    </div>
  );
}

